import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export function Footer() {
  const { t, pathFor, switchPath, otherLang } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-white/10 bg-anthracite-dark" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-8 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src="/logo-light.webp" alt="R-Mechanik SRLS Logo" className="h-24 w-auto" data-testid="footer-logo" />
          <p className="mt-4 text-sm leading-relaxed text-slate-400">{t.footer.description}</p>
          <p className="mt-6 font-mono-tech text-xs uppercase tracking-widest text-slate-500">
            R-Mechanik SRLS · Julian Rizzi<br />
            Pflerscherstraße 54<br />
            39041 Gossensaß / Colle Isarco (BZ)<br />
            Südtirol – Italien
          </p>
        </div>

        <div>
          <h3 className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{t.footer.quickNav}</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {["home", "services", "machines", "gallery", "about", "contact", "inquiry"].map((k) => (
              <li key={k}>
                <Link to={pathFor(k)} className="text-slate-400 transition-colors hover:text-white" data-testid={`footer-nav-${k}`}>
                  {t.nav[k]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{t.footer.servicesTitle}</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {t.footer.services.map((s) => (
              <li key={s}>
                <Link to={pathFor("services")} className="text-slate-400 transition-colors hover:text-white">{s}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{t.footer.contactTitle}</h3>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
            <li>
              <a href="tel:+393455807242" className="transition-colors hover:text-white" data-testid="footer-phone">+39 345 580 7242</a>
            </li>
            <li>
              <a href="mailto:info@r-mechanik.it" className="transition-colors hover:text-white" data-testid="footer-email">info@r-mechanik.it</a>
            </li>
            <li>{t.contactPage.hoursDays}<br />08:00–12:00 · 13:00–17:00</li>
          </ul>
          <div className="mt-5 flex gap-2" data-testid="footer-social">
            <a href="https://www.instagram.com/r.mechanik/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram"
              className="border border-white/15 p-2.5 text-slate-400 transition-colors hover:border-royal-soft hover:text-white">
              <Instagram size={16} />
            </a>
          </div>
          <h3 className="mt-8 font-mono-tech text-xs uppercase tracking-[0.3em] text-royal-soft">{t.footer.legalTitle}</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li><Link to={pathFor("imprint")} className="text-slate-400 hover:text-white" data-testid="footer-imprint">{t.footer.imprint}</Link></li>
            <li><Link to={pathFor("privacy")} className="text-slate-400 hover:text-white" data-testid="footer-privacy">{t.footer.privacy}</Link></li>
            <li><Link to={pathFor("cookies")} className="text-slate-400 hover:text-white" data-testid="footer-cookies">{t.footer.cookies}</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-6 sm:flex-row sm:px-8">
          <p className="font-mono-tech text-xs text-slate-500" data-testid="footer-copyright">
            © {year} R-Mechanik SRLS · {t.footer.rights}
          </p>
          <Link
            to={switchPath}
            className="font-mono-tech text-xs uppercase tracking-widest text-slate-400 transition-colors hover:text-white"
            data-testid="footer-lang-switcher"
          >
            {otherLang === "it" ? "Versione italiana → IT" : "Deutsche Version → DE"}
          </Link>
        </div>
      </div>
    </footer>
  );
}
